import { classNames } from '@dimjs/utils';
import { flatbizDate, isIphone, toArray } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FlexLayout } from '../flex-layout';
import { Grid } from '../grid';
import { useEffectCustom, usePropsValue } from '../hooks';
import { InVisibleView } from '../in-visible-view';
import { NativeScroll } from '../native-scroll';
import { DayItem } from './compts/day-item';
import { CalendarTips } from './compts/tips';
import { WeekList } from './compts/week-list';
import './style.less';
import { CalendarProps, DayTagItem } from './types';
import { calculateMonthList } from './utils';
export type CalendarRefApi = {
  scrollToTarget: () => void;
};

/**
 * 日历组件
 * @param props
 * @returns
 * ```
 * 1. 涉及到时间的 格式均为：YYYY-MM-DD（包括：minDate、maxDate、value、defaultValue等）
 * 2. dayTagList 配置日历标签，例如：节假日显示
 * 3. selectedType 选择类型，单选、双选；默认值：two-select
 * 4. weekType: 星期显示格式，周日是否显示在第一列；默认值：week7-first
 * 5. 虚拟dom渲染
 *    androd: 支持前后虚拟dom渲染
 *    iphone: 支持后虚拟dom渲染（前dom在虚拟变更时webview会上移，固无法实现前虚拟dom效果）
 * ```
 */
export const Calendar = forwardRef<CalendarRefApi, CalendarProps>((props, ref) => {
  const id = hooks.useId(undefined, 'calendar');
  console.log('id', id);
  const classPrefix = 'v-calendar';
  const firstRenderRef = useRef<boolean>(true);
  const [toggleIndex, setToggleIndex] = useState<number>(-1);
  const inViewListRef = useRef<Record<number, boolean>>({});
  const [showView, setShowView] = useState(false);
  /**
   * 单向虚拟dom渲染数量
   * 1. 页面总渲染数量 3 + 1 + 3
   */
  const virtualDomNum = 3;

  const inputValue = useMemo(() => {
    const valueList = toArray<string>(props.value);
    return valueList.length === 0 ? undefined : valueList;
  }, [props.value]);

  const inputDefaultValue = useMemo(() => {
    if (!firstRenderRef.current) return undefined;
    const valueList = toArray<string>(props.defaultValue);
    return valueList.length === 0 ? undefined : valueList;
  }, [props.defaultValue]);

  const [selectedList, setSelectedList] = usePropsValue({
    value: inputValue,
    defaultValue: inputDefaultValue,
    onChange: (data) => {
      const dataList = toArray<string>(data);
      if (props.selectedType === 'one-select') {
        props.onChange(dataList[0]);
        return;
      }
      props.onChange(dataList);
    },
  });

  const dayTagMap = useMemo(() => {
    const today = flatbizDate.format(new Date());
    const dayTagMap: Record<string, DayTagItem> = {
      [`${today}`]: {
        date: today,
        label: '今天',
      },
    };
    props.dayTagList?.forEach((item) => {
      dayTagMap[flatbizDate.format(item.date)] = item;
    });
    return dayTagMap;
  }, [props.dayTagList]);

  const monthAllList = useMemo(() => {
    return calculateMonthList(props.minDate, props.maxDate, props.weekType, props.isFill);
  }, [props.isFill, props.maxDate, props.minDate, props.weekType]);

  //  受控操作状态，计算
  const currentIndex = useMemo(() => {
    let value = inputValue?.[0] || inputDefaultValue?.[0];
    if (toggleIndex === -1) {
      value = value || flatbizDate.format(new Date());
      const filterIndex = monthAllList.findIndex((temp) => {
        return (
          flatbizDate.format(temp.monthDate, 'YYYY-MM') === flatbizDate.format(value as string, 'YYYY-MM')
        );
      });
      return filterIndex === -1 ? 0 : filterIndex;
    }
    return toggleIndex;
  }, [inputDefaultValue, inputValue, monthAllList, toggleIndex]);

  const monthShowList = useMemo(() => {
    let prevIndex = 0;
    if (!isIphone()) {
      prevIndex = currentIndex - virtualDomNum < 0 ? 0 : currentIndex - virtualDomNum;
    }
    const nextIndex = currentIndex + virtualDomNum;
    const prevList = monthAllList.slice(prevIndex, currentIndex);
    const nextList = monthAllList.slice(currentIndex + 1, nextIndex + 1);
    return prevList.concat(monthAllList[currentIndex]).concat(nextList);
  }, [currentIndex, monthAllList]);

  const onClickMonthDay = hooks.useCallbackRef((selectedDate: string[]) => {
    setSelectedList(selectedDate);
    if (toggleIndex === -1 && selectedDate && selectedDate.length > 0) {
      const selectedMD = flatbizDate.format(selectedDate[0], 'YYYY-MM');
      const target = monthShowList.find((item) => {
        return flatbizDate.format(item.monthDate, 'YYYY-MM') === selectedMD;
      });
      if (target) {
        setToggleIndex(target.index);
      }
    }
  });

  useEffectCustom(() => {
    // 初始化日历定位
    if (toggleIndex === -1 && monthAllList.length > 0) {
      const monthDateFt = flatbizDate.format(monthAllList[currentIndex].monthDate, 'YYYYMM');
      setTimeout(() => {
        document.querySelector(`#${id}-${monthDateFt}`)?.scrollIntoView({
          block: 'start',
        });
      }, 100);
    }
  }, []);

  useImperativeHandle(ref, () => {
    return {
      scrollToTarget: () => {
        const monthDateFt = flatbizDate.format(monthAllList[currentIndex].monthDate, 'YYYYMM');
        document.querySelector(`#${id}-${monthDateFt}`)?.scrollIntoView({
          block: 'start',
        });
      },
    };
  });

  const inVisibleChange = hooks.useCallbackRef((item, inView) => {
    if (inView) {
      if (!inViewListRef.current[item.index]) {
        inViewListRef.current[item.index] = true;
        setToggleIndex(item.index);
      }
    } else {
      if (inViewListRef.current[item.index]) {
        delete inViewListRef.current[item.index];
      }
    }
  });

  const onScroll = hooks.useCallbackRef(() => {
    if (!showView && monthShowList.length > virtualDomNum) {
      setShowView(true);
    }
  });

  const className = classNames(classPrefix, { [`${classPrefix}-sticky`]: props.titleSticky });
  return (
    <FlexLayout className={className} fullIndex={1} style={props.style}>
      <div className={`${classPrefix}-header`}>
        <WeekList weekType={props.weekType} />
        <CalendarTips tips={props.tips} onTips={props.onTips} />
      </div>
      <NativeScroll className={`${classPrefix}-scroll`} onScroll={onScroll}>
        {monthShowList.map((item) => {
          return (
            <div key={item.index} className={`${classPrefix}-item`}>
              {showView ? <InVisibleView onChange={inVisibleChange.bind(null, item)} /> : null}
              <div id={`${id}-${flatbizDate.format(item.monthDate, 'YYYYMM')}`}></div>
              {!props.hideMonthTitle ? (
                <div className={`${classPrefix}-month-title`}>{item.month}</div>
              ) : null}
              <Grid columns={7} gap={0} className="v-calendar-grid">
                {item.dayList.map((dayItem, dayIndex) => (
                  <DayItem
                    key={dayIndex}
                    classPrefix={'v-calendar'}
                    dayItem={dayItem}
                    onClickDay={onClickMonthDay}
                    selectedList={selectedList}
                    selectedType={props.selectedType}
                    onPreChange={props.onPreChange}
                    selectedStartTag={props.selectedStartTag}
                    selectedEndTag={props.selectedEndTag}
                    dayTagMap={dayTagMap}
                    isFill={props.isFill}
                    onDisabledClick={props.onDisabledClick}
                  />
                ))}
              </Grid>
            </div>
          );
        })}
      </NativeScroll>
    </FlexLayout>
  );
});

Calendar.defaultProps = {
  weekType: 'week7-first',
  selectedType: 'two-select',
};
